import { asMutableType, bd, deepFreeze, } from '@tymia/shared';
import { mapValues, omit, pick, } from 'remeda';
import { searchBookableAppointmentsResponseSchema, } from '../appointment/AppointmentApi.js';
import { supportedLocales, } from '../translation/translation.js';
import { nullableEnum, pagingRequest, pagingResponse, } from '../validator.js';
export const pricing = mapValues({
    therapist: {
        EUR: '40.00',
    },
}, mapValues((amount, currency) => ({
    amount,
    amountBd: bd(amount),
    currency,
})));
export const userModuleUrl = import.meta.url;
export const salutation = asMutableType(Object.freeze([
    'Mr.',
    'Ms.',
    'Diverse',
]));
export const Roles = {
    guest: 1,
    registered: 2,
    admin: 4,
    assistant: 8,
    // TODO: rename into specialist
    therapist: 16,
    customeradmin: 32,
    therapistdrs: 64,
};
export const userInsuranceType = asMutableType(Object.freeze([
    'legally',
    'privately',
]));
// ordering is important for representation in UI
export const treatmentMethodType = deepFreeze([
    'Behavioral Therapy',
    'Psychodynamic Therapy',
    'Coaching',
    'Systemic Therapy',
    'Psychological Counseling',
    'Schema Therapy',
    'Relaxation Techniques',
    'Dialectical Behavior Therapy',
    'Person-Centered Therapy',
    'Hypnotherapy',
    'Integrative Therapy',
    'Somatic Therapy',
    'EMDR',
    'Humanistic Therapy',
    'Art Therapy',
    'Psychoanalysis',
    'Supervision',
]);
export const therapeuticFocusType = deepFreeze([
    'ADHD',
    'Anxiety and Panic Disorders',
    'Adjustment Disorder',
    'Bipolar Disorder',
    'Burnout',
    'Social Phobia',
    'Generalized Anxiety Disorder',
    'Agoraphobia',
    'Depression',
    'Borderline Disorder',
    'Eating Disorders',
    'Family Therapy',
    'Group Therapy',
    'Couples Therapy',
    'Personality Disorders',
    'Post-Traumatic Stress Disorder',
    'Psycho-Oncology',
    'Psychocardiology',
    'Psychosis - Schizophrenia',
    'Psychosomatic Disorders',
    'Pain',
    'Sexuality',
    'Addiction',
    'Child and Adolescent Therapy',
    'Grief',
    'Obsessive-Compulsive Disorder',
]);
export const coverageTypes = deepFreeze([
    'Private Health Insurance',
    'Statutory Health Insurance',
    'Self-Payment',
    'Reimbursement Procedure',
]);
export const userJobTitleType = asMutableType(Object.freeze([
    'psychologicalPsychotherapist',
    'psychologicalPsychotherapistChildren',
    'specialistPsychiatryPsychotherapy',
    'specialistPsychiatryPsychotherapyChildren',
    'specialistPsychosomatics',
    'physicianAdditionalQualificationPsychotherapy',
    'nonMedicalPractitionerPsychotherapy',
    'socialPedagogue',
    'coachOrConsultant',
]));
export const advertisingType = asMutableType(Object.freeze([
    'no',
    'show',
]));
export const invoiceAssessmentSessionsType = asMutableType(Object.freeze([
    'mark',
    'nomark',
]));
export const invoicePatientNumberType = asMutableType(Object.freeze([
    'show',
    'hide',
]));
export const appointmentOnsiteNotificationDefaultType = asMutableType(Object.freeze([
    'on',
    'off',
]));
export const salutationSearchableType = asMutableType(Object.freeze([
    'yes',
    'no',
]));
export const invoiceDigitalType = asMutableType(Object.freeze([
    'yes',
    'no',
]));
export const genderType = asMutableType(Object.freeze([
    'female',
    'male',
    'diverse',
]));
export function genderToSalutation(gender) {
    return {
        female: 'Ms.',
        male: 'Mr.',
        diverse: 'Diverse',
    }[gender];
}
export const locationType = asMutableType(Object.freeze([
    'onSite',
    'remote',
]));
export const publicVisibilityType = asMutableType(Object.freeze([
    'public',
    'private',
]));
export const searchTherapistSortType = asMutableType(Object.freeze([
    'distance',
    'availability',
]));
export const userCommonOpenApiFields = {
    id: {
        type: 'number',
    },
    salutation: nullableEnum(salutation),
    title: {
        type: ['string', 'null'],
        nullable: true,
    },
    firstName: {
        type: 'string',
        minLength: 1,
    },
    lastName: {
        type: 'string',
        minLength: 1,
    },
    roles: {
        type: 'number',
        enum: [
            Roles.admin,
            Roles.assistant,
            Roles.therapist,
            Roles.customeradmin,
            Roles.customeradmin + Roles.therapist,
            Roles.therapistdrs,
        ],
    },
    email: {
        type: 'string',
        format: 'idn-email',
    },
    phoneCountry: {
        type: ['string', 'null'],
        nullable: true,
    },
    phone: {
        type: ['string', 'null'],
        nullable: true,
    },
    pinTransportMethod: {
        type: 'string',
        enum: ['none', 'email'],
    },
    deleted: {
        type: 'boolean',
    },
    street: {
        type: ['string', 'null'],
        nullable: true,
    },
    postalCode: {
        type: ['string', 'null'],
        nullable: true,
    },
    city: {
        type: ['string', 'null'],
        nullable: true,
    },
    languages: {
        type: 'array',
        items: {
            type: 'string',
            minLength: 2,
            maxLength: 2,
        },
        minItems: 1,
    },
};
export const userClientOpenApiFields = {
    // therapist or assistant can set patientNumber manually
    patientNumber: {
        type: ['string', 'number', 'null'],
        nullable: true,
    },
    birthday: {
        type: ['string', 'null'],
        format: 'date',
        nullable: true,
    },
    insuranceType: nullableEnum(userInsuranceType),
    defaultUserDiagnosisId: {
        type: ['number', 'null'],
        nullable: true,
    },
    internalNote: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceRemark: {
        type: ['string', 'null'],
        nullable: true,
    },
    careOf: {
        type: ['string', 'null'],
        nullable: true,
    },
    country: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceAddress: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceDigital: {
        type: ['string', 'null'],
        enum: [...invoiceDigitalType, null],
        nullable: true,
    },
};
export const userSpecialistOpenApiFields = {
    avatar: {
        type: ['string', 'null'],
        nullable: true,
    },
    website: {
        type: ['string', 'null'],
        nullable: true,
    },
    // it's required only for therapists
    // "other" is send as `null`
    jobTitleType: {
        type: ['string', 'null'],
        enum: [...userJobTitleType, null],
        nullable: true,
    },
    jobTitleFreetext: {
        type: ['string', 'null'],
        nullable: true,
    },
    treatmentMethods: {
        type: 'array',
        items: {
            type: 'string',
        },
    },
    therapeuticFocus: {
        type: 'array',
        items: {
            type: 'string',
        },
    },
    coverageTypes: {
        type: 'array',
        items: {
            type: 'string',
            enum: coverageTypes,
        },
    },
    therapistProfileText: {
        type: ['string', 'null'],
        nullable: true,
    },
    billingInformation: {
        type: ['string', 'null'],
        nullable: true,
    },
    vita: {
        type: ['string', 'null'],
        nullable: true,
    },
    billingApproval: {
        type: ['boolean', 'null'],
        nullable: true,
    },
    invoiceLetterhead: {
        type: ['string', 'null'],
        nullable: true,
    },
    returnAddress: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceSignature: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceAssessmentSessions: {
        type: ['string', 'null'],
        enum: [...invoiceAssessmentSessionsType, null],
        nullable: true,
    },
    invoicePatientNumber: {
        type: ['string', 'null'],
        enum: [...invoicePatientNumberType, null],
        nullable: true,
    },
    appointmentOnsiteNotificationDefault: {
        type: ['string', 'null'],
        enum: [...appointmentOnsiteNotificationDefaultType, null],
        nullable: true,
    },
    advertising: {
        type: ['string', 'null'],
        enum: [...advertisingType, null],
        nullable: true,
    },
    salutationSearchable: {
        type: 'string',
        enum: salutationSearchableType,
    },
    publicVisibility: {
        type: ['string', 'null'],
        enum: [...publicVisibilityType, null],
        nullable: true,
    },
};
// * searchTherapist
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function searchTherapistRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            locale: {
                type: 'string',
                enum: supportedLocales(),
            },
            address: {
                type: ['string', 'null'],
                minLength: 1,
                maxLength: 1_000,
            },
            therapeuticFocus: {
                type: 'array',
                items: {
                    type: 'string',
                },
                minItems: 0,
                maxItems: 1_000,
                uniqueItems: true,
            },
            treatmentMethods: {
                type: 'array',
                items: {
                    type: 'string',
                },
                minItems: 0,
                maxItems: 1_000,
                uniqueItems: true,
            },
            jobTitleTypes: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: userJobTitleType,
                },
                minItems: 0,
                maxItems: 1_000,
                uniqueItems: true,
            },
            languages: {
                type: 'array',
                items: {
                    type: 'string',
                    minLength: 2,
                    maxLength: 2,
                },
                minItems: 0,
                maxItems: 1_000,
                uniqueItems: true,
            },
            coverageTypes: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: coverageTypes,
                },
                minItems: 0,
                maxItems: 1_000,
                uniqueItems: true,
            },
            gender: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: genderType,
                },
                minItems: 0,
                maxItems: 3,
                uniqueItems: true,
            },
            location: {
                type: 'array',
                items: {
                    type: 'string',
                    enum: locationType,
                },
                minItems: 0,
                maxItems: 2,
                uniqueItems: true,
            },
            sort: {
                type: ['string', 'null'],
                enum: ['distance', 'availability', null],
            },
            // filtering inside the BookingComponent
            source: {
                type: 'string',
                enum: ['widget', 'search'],
            },
            therapistId: {
                type: 'string',
                format: 'decimal',
            },
            insuranseType: {
                type: 'string',
                enum: userInsuranceType,
            },
            meetingTypeId: {
                type: 'string',
                format: 'decimal',
            },
            appointmentsFrom: {
                type: 'string',
                format: 'date-time',
            },
            ...pagingRequest.properties,
        },
        required: [
            'locale',
        ],
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function searchTherapistSearchParamsSchema() {
    const value = searchTherapistRequestSchema();
    return {
        ...value,
        properties: omit(value.properties, ['locale', 'source']),
        required: value.required.filter(r => r !== 'locale'),
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function searchTherapistResponseSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            status: {
                const: 'ok',
            },
            city: {
                type: 'object',
                additionalProperties: false,
                properties: {
                    name: {
                        type: 'string',
                    },
                    description: {
                        type: ['string', 'null'],
                        nullable: true,
                    },
                    content: {
                        type: ['string', 'null'],
                        nullable: true,
                    },
                },
            },
            languages: {
                type: 'array',
                items: {
                    type: 'string',
                    minLength: 2,
                    maxLength: 2,
                },
                uniqueItems: true,
            },
            list: {
                type: 'array',
                items: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                        ...pick(userCommonOpenApiFields, [
                            'salutation',
                            'phone',
                            'languages',
                            'street',
                            'postalCode',
                            'city',
                        ]),
                        id: {
                            type: 'string',
                            format: 'decimal',
                        },
                        displayName: {
                            type: 'string',
                        },
                        ...pick(userSpecialistOpenApiFields, [
                            'avatar',
                            'website',
                            'jobTitleType',
                            'jobTitleFreetext',
                            'therapistProfileText',
                            'billingInformation',
                            'treatmentMethods',
                            'therapeuticFocus',
                            'coverageTypes',
                        ]),
                        customerId: {
                            type: ['string', 'null'],
                            nullable: true,
                            format: 'decimal',
                        },
                        distance: {
                            type: 'string',
                            format: 'decimal',
                        },
                        nextFreeAppointment: {
                            type: ['string', 'null'],
                            nullable: true,
                            format: 'date-time',
                        },
                        // set only for advertising
                        nextFreeRemoteAppointment: {
                            type: ['string', 'null'],
                            nullable: true,
                            format: 'date-time',
                        },
                        type: {
                            type: 'string',
                            enum: [
                                'ad',
                                'available',
                                'with avatar',
                                'without avatar',
                                'external',
                            ],
                        },
                        appointments: searchBookableAppointmentsResponseSchema(),
                    },
                    required: [
                        'id',
                        'displayName',
                        'languages',
                        'street',
                        'postalCode',
                        'city',
                        'treatmentMethods',
                        'therapeuticFocus',
                        'coverageTypes',
                        'customerId',
                        'distance',
                        'nextFreeAppointment',
                        'nextFreeRemoteAppointment',
                        'type',
                    ],
                },
                uniqueItems: true,
            },
            ...pagingResponse.properties,
        },
        required: [
            'status',
            'city',
            'languages',
            'list',
            ...pagingResponse.required,
        ],
    };
}
export async function searchTherapist(baseUrl, props) {
    const result = await fetch(`${baseUrl}/api/therapist/search`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(props),
    });
    if (result.ok) {
        const data = await result.json();
        return data;
    }
    return {
        status: 'network error',
        httpStatus: result.status,
        message: await result.text(),
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function postClientRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            customerId: {
                type: 'number',
            },
            salutation: nullableEnum(salutation),
            title: {
                type: ['string', 'null'],
                nullable: true,
            },
            firstName: {
                type: 'string',
            },
            lastName: {
                type: 'string',
            },
            email: {
                type: 'string',
                format: 'idn-email',
            },
            phoneCountry: {
                type: ['string', 'null'],
                nullable: true,
            },
            phone: {
                type: ['string', 'null'],
                nullable: true,
            },
            language: {
                type: ['null'],
                nullable: true,
            },
            insuranceType: nullableEnum(userInsuranceType),
            birthday: {
                type: ['string', 'null'],
                format: 'date',
                nullable: true,
            },
            // therapist or assistant can set patientNumber manually
            patientNumber: {
                type: ['string', 'null'],
                nullable: true,
            },
            street: {
                type: ['string', 'null'],
                nullable: true,
            },
            postalCode: {
                type: ['string', 'null'],
                nullable: true,
            },
            city: {
                type: ['string', 'null'],
                nullable: true,
            },
            careOf: {
                type: ['string', 'null'],
                nullable: true,
            },
            country: {
                type: ['string', 'null'],
                nullable: true,
            },
            internalNote: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceRemark: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceAddress: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceDigital: {
                type: ['string', 'null'],
                enum: [...invoiceDigitalType, null],
                nullable: true,
            },
        },
        required: [
            'customerId',
            'firstName',
            'lastName',
            'email',
        ],
    };
}
//# sourceMappingURL=UserApi.js.map